import {
  Button,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";

import { errorMessage } from "@/lib/api";
import { formatAmount } from "@/lib/format";
import { QUOTATION_STATUS_LABEL, type Quotation } from "./api";

/** 確認のために打ってもらう言葉。 */
const CONFIRM_WORD = "削除";

/**
 * 選んだ見積をまとめて削除する確認ダイアログ。
 *
 * **見積は 1 件でも抱えているものが多い**（明細・工程別見積・取り込んだ図面）。
 * 消える範囲が行数では読めないので、共通の [[ConfirmDialog]]（文章 1 つ）では
 * なく専用のダイアログにしてある。ここで見せるのは 3 つ:
 *
 * - **何が消えるか** … 見積ごとの明細行数と金額を一覧で出す。「合計 N 行」だけ
 *   だと、金額の入った見積が混ざっていても気づけない
 * - **何が残るか** … 図面 PDF は消えない（見積から外れるだけ）。消えたと思って
 *   上げ直されるのを防ぐ
 * - **止まる条件** … 案件に紐付いた見積が 1 件でも混ざると、まとめて中止する
 *
 * そのうえで**「削除」と打ってもらう**。押し間違いで消えると、明細の入力を
 * やり直すことになり取り返しがつかない。
 */
export default function QuotationBulkDeleteDialog({
  isOpen,
  quotations,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  /** 画面で選ばれている見積。 */
  quotations: Quotation[];
  onClose: () => void;
  /** 削除を実行する。失敗したらそのまま投げ返すと、理由をここに出す。 */
  onConfirm: () => Promise<void>;
}) {
  const [word, setWord] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 開くたびに入力と前回の失敗理由を捨てる。持ち越すと、打ち直さずに
  // 削除できてしまう。
  useEffect(() => {
    if (!isOpen) return;
    setWord("");
    setError(null);
  }, [isOpen]);

  const itemCount = quotations.reduce((sum, q) => sum + q.item_count, 0);
  const totalAmount = quotations.reduce((sum, q) => sum + Number(q.total_amount), 0);
  // 受注済みの見積は案件に紐付いていることが多く、まず消せない。選択から外す
  // 相手を先に教える。
  const ordered = quotations.filter((q) => q.status === "ordered");

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
    } catch (e) {
      setError(errorMessage(e, "削除に失敗しました。"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()} size="2xl">
      <ModalContent>
        <ModalHeader className="flex items-center gap-2 text-danger">
          <AlertTriangle size={20} />
          選んだ見積の削除
        </ModalHeader>
        <ModalBody>
          <p className="text-sm">
            見積 <span className="font-bold">{quotations.length} 件</span>を、明細{" "}
            <span className="font-bold">{itemCount} 行</span>（合計{" "}
            {formatAmount(String(totalAmount))} 円）ごと削除します。
            <br />
            <span className="font-bold text-danger">この操作は取り消せません。</span>
          </p>

          <ul className="list-disc space-y-0.5 rounded-lg bg-default-50 px-6 py-3 text-sm text-default-600">
            <li>明細と、明細に入力した工程別見積（金額の内訳）も一緒に消えます。</li>
            {/* 日本語の文を JSX のテキストのまま折り返すと、改行が半角空白に
                なって「…どの見積にも 属さない…」と出てしまう。文字列式で持つ。 */}
            <li>
              {
                "取り込んだ図面 PDF は消えませんが、この見積から外れます（どの見積にも属さない図面になります）。"
              }
            </li>
            <li>
              案件に紐付いている見積は削除できません。1 件でも混ざっていると、
              <span className="font-bold">まとめて中止</span>します。
            </li>
          </ul>

          {ordered.length > 0 && (
            <p className="text-sm text-warning-600">
              {`受注済みの見積が ${ordered.length} 件あります（${ordered
                .map((q) => q.quotation_number)
                .join("、")}）。`}
              {"案件に紐付いていると削除できません。"}
            </p>
          )}

          <div className="max-h-56 overflow-auto rounded-lg border border-default-200">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-default-50 text-left text-tiny text-default-600">
                <tr className="h-8">
                  <th className="px-3 font-bold">見積番号</th>
                  <th className="px-3 font-bold">得意先</th>
                  <th className="px-3 font-bold">状態</th>
                  <th className="px-3 text-right font-bold">明細</th>
                  <th className="px-3 text-right font-bold">金額</th>
                </tr>
              </thead>
              <tbody>
                {quotations.map((quotation) => (
                  <tr
                    key={quotation.quotation_id}
                    className="h-9 border-t border-default-100"
                  >
                    <td className="px-3 font-medium">{quotation.quotation_number}</td>
                    <td className="truncate px-3">{quotation.client_name ?? "—"}</td>
                    <td className="px-3">
                      <Chip size="sm" variant="flat">
                        {QUOTATION_STATUS_LABEL[quotation.status]}
                      </Chip>
                    </td>
                    <td className="px-3 text-right">{quotation.item_count} 行</td>
                    <td className="px-3 text-right">
                      {formatAmount(quotation.total_amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Input
            label={`確認のため「${CONFIRM_WORD}」と入力してください`}
            labelPlacement="outside"
            variant="bordered"
            radius="sm"
            value={word}
            onValueChange={setWord}
            data-testid="quotation-bulk-delete-word"
            aria-label={`確認のため ${CONFIRM_WORD} と入力`}
          />

          {error && <p className="text-sm text-danger">{error}</p>}
        </ModalBody>
        <ModalFooter>
          <Button variant="light" radius="full" data-testid="cancel" onPress={onClose}>
            キャンセル
          </Button>
          <Button
            color="danger"
            radius="full"
            data-testid="confirm"
            isDisabled={word.trim() !== CONFIRM_WORD || quotations.length === 0}
            isLoading={busy}
            onPress={() => void confirm()}
          >
            {quotations.length} 件を削除
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
